import { useMemo, useState, useEffect, useRef } from "react";
import moment from "moment";
import { updateHours } from "../../services/newdaily";
import styles from "./TurnSelector.module.css";
import TurnOption from "./TurnOption.js";
import TurnHours from "./TurnHours.js";
import TurnType from "./TurnType.js";

const hasTime = (v) => {
  if (!v) return false;
  const s = String(v).trim();
  return s !== "" && s !== "00:00:00" && s !== "00:00";
};

const TurnSelector = ({
  selectedWeek,
  selectedDate,
  horasEmpleado,
  empleadoId,
  isMobile,
  fechaEmpleado,
  osViewport,
}) => {
  const days = useMemo(() => {
    const arr = [];
    if (selectedWeek?.start) {
      const start = moment(selectedWeek.start);
      for (let i = 0; i < 7; i++) arr.push(moment(start).add(i, "days"));
    }
    return arr;
  }, [selectedWeek]);

  const todayISO = moment().format("YYYY-MM-DD");
  const yesterday = moment().subtract(1, "day").startOf("day");

  const fechaEmpleadoMoment = useMemo(() => {
    if (!fechaEmpleado) return null;

    const m = moment(
      String(fechaEmpleado).trim(),
      ["YYYY-MM-DD", "YYYY-MM-DD HH:mm:ss", "DD/MM/YYYY"],
      true
    );

    return m.isValid() ? m.startOf("day") : null;
  }, [fechaEmpleado]);

  const initialByDate = useMemo(() => {
    const m = {};
    (horasEmpleado || []).forEach((h) => {
      m[h.Fecha] = h.Turno;
    });
    return m;
  }, [horasEmpleado]);

  const initialTipoByDate = useMemo(() => {
    const m = {};
    (horasEmpleado || []).forEach((h) => {
      m[h.Fecha] = h.Tipo_Tarde;
    });
    return m;
  }, [horasEmpleado]);

  const initialHoursByDate = useMemo(() => {
    const m = {};
    (horasEmpleado || []).forEach((h) => {
      m[h.Fecha] = { MI: h.MI, MF: h.MF, TI: h.TI, TF: h.TF };
    });
    return m;
  }, [horasEmpleado]);

  const [turnoByDate, setTurnoByDate] = useState(initialByDate);
  const [tipoByDate, setTipoByDate] = useState(initialTipoByDate);
  const [hoursByDate, setHoursByDate] = useState(initialHoursByDate);
  const [savingDate, setSavingDate] = useState(null);

  useEffect(() => {
    setTurnoByDate((prev) => ({ ...initialByDate, ...prev }));
    setTipoByDate((prev) => ({ ...initialTipoByDate, ...prev }));
  }, [initialByDate, initialTipoByDate]);

  useEffect(() => {
    setHoursByDate((prev) => ({ ...initialHoursByDate, ...prev }));
  }, [initialHoursByDate]);

  const updateTurno = async (fechaISO, nuevoTurno) => {
    setTurnoByDate((s) => ({ ...s, [fechaISO]: nuevoTurno }));
    setSavingDate(fechaISO);

    try {
      await updateHours({
        empleado: empleadoId,
        fecha: fechaISO,
        turno: nuevoTurno,
      });
    } catch (e) {
      console.error("Error guardando turno:", e);
      setTurnoByDate((s) => {
        const prev = { ...s };
        return prev;
      });
    } finally {
      setSavingDate(null);
    }
  };

  const dayRefs = useRef({});
  const listRef = useRef(null);

  const didInitialScrollRef = useRef(false);

  useEffect(() => {
    if (!selectedDate) return;
    if (!osViewport) return;

    const isInWeek = days.some((d) => d.format("YYYY-MM-DD") === selectedDate);
    if (!isInWeek) return;

    const el = dayRefs.current[selectedDate];
    if (!el) return;

    const doScroll = (behavior) => {
      const viewportRect = osViewport.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const delta = elRect.top - viewportRect.top;
      const targetTop = osViewport.scrollTop + delta - 8;

      osViewport.scrollTo({
        top: Math.max(0, targetTop),
        behavior,
      });
    };

    if (!didInitialScrollRef.current) {
      doScroll("auto");
      const t = setTimeout(() => {
        doScroll("auto");
        didInitialScrollRef.current = true;
      }, 60);

      return () => clearTimeout(t);
    }
  
    doScroll("smooth");
  }, [selectedDate, days, osViewport]);


  return (
    <div ref={listRef} className={styles.turnSelector}>
      {days.map((day, index) => {
        const fecha = day.format("YYYY-MM-DD");
        const isToday = fecha === todayISO;

        const datosDelDia = horasEmpleado.find((h) => h.Fecha === fecha);

        const turnoActual = turnoByDate[fecha] ?? datosDelDia?.Turno ?? null;
        const tipoActual = tipoByDate[fecha] ?? datosDelDia?.Tipo_Tarde ?? null;

        const baseHours = {
          MI: datosDelDia?.MI,
          MF: datosDelDia?.MF,
          TI: datosDelDia?.TI,
          TF: datosDelDia?.TF,
        };
        const effHours = { ...baseHours, ...(hoursByDate[fecha] || {}) };

        const hasMI = hasTime(effHours.MI);
        const hasMF = hasTime(effHours.MF);
        const hasTI = hasTime(effHours.TI);
        const hasTF = hasTime(effHours.TF);

        const tipoEfectivo = Number(tipoActual ?? datosDelDia?.Tipo_Tarde ?? 0);

        const missingRecord =
          tipoEfectivo === 1
            ? !(hasMI && hasMF && hasTI && hasTF)
            : !(hasMI && hasMF);

        const dayMoment = moment(fecha, "YYYY-MM-DD").startOf("day");
        const isPastFromYesterday = dayMoment.isSameOrBefore(yesterday, "day");

        const passesFechaEmpleadoLimit = fechaEmpleadoMoment
          ? dayMoment.isSameOrAfter(fechaEmpleadoMoment, "day")
          : true;

        const shouldMarkMissing =
          missingRecord && isPastFromYesterday && passesFechaEmpleadoLimit;

        return (
          <div
            key={index}
            className={styles.dayCard}
            ref={(node) => {
              if (node) dayRefs.current[fecha] = node;
            }}
          >
            <div
              className={[
                styles.dayRow,
                isToday ? styles.today : "",
                !isToday && Number(turnoActual) === 3 ? styles.turno3 : "",
                !isToday && shouldMarkMissing ? styles.missingDayRow : "",
              ].join(" ")}
            >
              {day.format("dddd, D ")}
              <span className={styles.noCapitalize}>de</span>
              {day.format(" MMMM")}
            </div>

            <div
              className={`${styles.dataColumn} ${isMobile ? styles.dataColumnMobile : ""
                }`}
            >
              <div className={styles.dataContainer}>
                <TurnOption
                  activeTurno={turnoActual}
                  disabled={savingDate === fecha}
                  onSelect={(nuevo) => updateTurno(fecha, nuevo)}
                  isMobile={isMobile}
                />
              </div>

              {Number(turnoActual) === 1 && (
                <TurnType
                  fecha={fecha}
                  empleadoId={empleadoId}
                  value={tipoActual}
                  onChange={(val) =>
                    setTipoByDate((s) => ({ ...s, [fecha]: val }))
                  }
                />
              )}

              {(Number(turnoActual) === 1 || Number(turnoActual) === 2) && (
                <TurnHours
                  turno={Number(turnoActual)}
                  mi={effHours.MI}
                  mf={effHours.MF}
                  ti={effHours.TI}
                  tf={effHours.TF}
                  fecha={fecha}
                  empleadoId={empleadoId}
                  tipoTarde={tipoActual}
                  isMobile={isMobile}
                  onLocalUpdate={(patch) => {
                    setHoursByDate((s) => ({
                      ...s,
                      [fecha]: { ...(s[fecha] || {}), ...patch },
                    }));
                  }}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TurnSelector;
